import React, { useEffect, useMemo } from 'react';
import CalendarView from '../CalendarView';
import authBackground from '../../assets/auth-background.jpg';
import { ActionItemsProps } from './types';
import { useTasks, useDragDrop } from './hooks';
import { BoardColumn } from './components';
import '../ActionItems.css';

const ActionItems: React.FC<ActionItemsProps> = ({ onFounderClick }) => {
  const {
    actionItems,
    setActionItems,
    loading,
    allFounders,
    editingCard,
    editValues,
    setEditValues,
    creatingCard,
    setCreatingCard,
    newCardValues,
    setNewCardValues,
    loadActionItems,
    handleStatusChange,
    getItemsByStatus,
    handleEditClick,
    hasEdits,
    handleEditSave,
    handleEditCancel,
    handleCreateClick,
    handleCreateSave,
    handleCreateCancel,
    handleDelete,
  } = useTasks();

  const {
    draggedItem,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
    handleCardDragOver,
  } = useDragDrop({
    actionItems,
    setActionItems,
    handleStatusChange,
  });

  useEffect(() => {
    loadActionItems();
  }, [loadActionItems]);

  const cloudBackgroundStyle = useMemo(
    () => ({ '--cloud-bg': `url(${authBackground})` } as React.CSSProperties),
    []
  );

  if (loading) {
    return (
      <div className="action-items-container" style={cloudBackgroundStyle}>
        <div className="loading">Loading actions...</div>
      </div>
    );
  }

  return (
    <div className="action-items-container" style={cloudBackgroundStyle}>
      {/* Calendar View - Always visible on top */}
      <CalendarView
        tasks={actionItems.map((item) => ({
          id: item.id,
          title: item.title,
          description: item.description,
          status: item.status,
          dueDate: item.dueDate,
          recurrence: item.recurrence,
          nextDueDate: item.nextDueDate,
          founderId: item.founderId,
          Founder: item.Founder ? { name: item.Founder.name } : undefined,
        }))}
        onTaskClick={(task) => {
          const actionItem = actionItems.find((item) => item.id === task.id);
          if (actionItem && onFounderClick) {
            onFounderClick(actionItem.founderId);
          }
        }}
        onCreateTask={(date) => {
          const formattedDate = date.toISOString().split('T')[0];
          setNewCardValues((prev) => ({ ...prev, dueDate: formattedDate }));
          setCreatingCard('pending');
        }}
      />

      {/* Board View - Always visible below calendar */}
      <div className="trello-board" style={{ marginTop: '2rem' }}>
        <BoardColumn
          title="TO DO"
          status="pending"
          items={getItemsByStatus('pending')}
          creatingCard={creatingCard}
          newCardValues={newCardValues}
          setNewCardValues={setNewCardValues}
          editingCard={editingCard}
          editValues={editValues}
          setEditValues={setEditValues}
          draggedItem={draggedItem}
          allFounders={allFounders}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onCardDragOver={handleCardDragOver}
          onCreateClick={handleCreateClick}
          onCreateSave={handleCreateSave}
          onCreateCancel={handleCreateCancel}
          onEditClick={handleEditClick}
          onEditSave={handleEditSave}
          onEditCancel={handleEditCancel}
          onDelete={handleDelete}
          hasEdits={hasEdits}
          onFounderClick={onFounderClick}
        />

        <BoardColumn
          title="DONE"
          status="completed"
          items={getItemsByStatus('completed')}
          creatingCard={creatingCard}
          newCardValues={newCardValues}
          setNewCardValues={setNewCardValues}
          editingCard={editingCard}
          editValues={editValues}
          setEditValues={setEditValues}
          draggedItem={draggedItem}
          allFounders={allFounders}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onCardDragOver={handleCardDragOver}
          onCreateClick={handleCreateClick}
          onCreateSave={handleCreateSave}
          onCreateCancel={handleCreateCancel}
          onEditClick={handleEditClick}
          onEditSave={handleEditSave}
          onEditCancel={handleEditCancel}
          onDelete={handleDelete}
          hasEdits={hasEdits}
          onFounderClick={onFounderClick}
          showReminder={false}
        />
      </div>
    </div>
  );
};

export default ActionItems;
